import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import ScorecardNavigation from './ScorecardNavigation';
import MonthFilter from './MonthFilter';
import ErrorMessage from '../common/ErrorMessage';
import LoadingSpinner from '../common/LoadingSpinner';
import { getTenantAvailableMonths } from '../../services/scopedReadsService';
import { useApiError } from '../../hooks/useApiError';
import { config } from '../../config/environment';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import managementService from '../../services/managementService';
import PinnedHeaderTable from './stickyTableHeader/PinnedHeaderTable';
import RoleGridHeading from './RoleGridHeading';
import { groupsOf, visibleGroups, showRoleHeadings } from '../../services/scorecardGroups';
import {
  getServerScorecard,
  FAKE_M2_TENANT_ID,
  FAKE_MR_TENANT_ID,
  ServerScorecardForbiddenError,
  type ServerScorecardRow,
  type ServerScorecardGroup,
  type MetricColumn,
  type DrinkColumn,
} from '../../services/serverScorecardService';

/**
 * feat-088 Server Scorecard grid (refocus — spec F1/F2, ADR 0001 D-7).
 *
 * Per-server aggregate columns from GET /api/server-scorecard/{tenant_id}:
 *   Server · Score · Visits · ≤7 tenant-flagged metric sums · [drink] · Details
 *   (feat-296: ≥5 metric columns switch to the dense grid — DenseScorecardTable).
 *
 * All values are rendered VERBATIM from the backend (no React recompute —
 * feedback_metrics_single_source):
 *   - Score   = the VISIT-WEIGHTED POOLED rate over the window (feat-234):
 *     Σ upsell attempts ÷ Σ visits, so a busy shift counts for more than a quiet
 *     one. It is NOT an average of per-shift scores — that unweighted mean was
 *     what feat-234 removed. The backend formats it (`score_display`, which
 *     carries a `%` suffix when the tenant's score_multiplier is 1) and React
 *     renders that string as-is; React never divides. Per human decision O1 we
 *     do NOT render the tier word or color band, even though the response
 *     carries score_tier/score_band: the BespokeScore has no ceiling, so the old
 *     0–100 thresholds would falsely paint everyone red (feat-084 trap).
 *   - Greetings + each metric column = SUMS across in-window shifts (AC#4).
 *   - The metric columns (labels + order) come from the response `metric_columns`
 *     — dynamic, tenant-configurable, capped at 7 by the backend (feat-296).
 *
 * Null semantics: a server with 0 in-window shifts renders "—" for Score,
 * Greetings, and every metric (`null`); a server WITH shifts but no hits on a
 * metric renders a real "0". These two cases are deliberately distinct.
 *
 * "View Details →" navigates to the Performance History page (unchanged — D4/F4),
 * passing server_id + tenant_id + serverName.
 */

// feat-091 Slice C — id for the aggregate Score-cell tooltip (D-5).
const SCORE_TOOLTIP_ID = 'scorecard-score-tooltip';

// feat-296: the grid switches to the DENSE layout when there are MORE than this
// many metric columns. The same number as the backend's SCORECARD_FALLBACK_COLUMNS
// and the pre-feat-296 cap, so every tenant at ≤4 keeps today's markup. The drink
// column does not count toward it. Named locally on purpose (design NOTES §2).
const DENSE_AFTER = 4;

/**
 * feat-097 Part A — client-side scoping rule (exported for unit testing). Keeps
 * only rows whose `location_id` is in the allowed set (the live locations the
 * Location dropdown offers). Pure: no React, no async — the component memoizes a
 * call to this for the "All Locations" admin/corporate view.
 */
export function scopeRowsToLocationSet(
  rows: ServerScorecardRow[],
  allowedLocationIds: Array<string | number>,
): ServerScorecardRow[] {
  const allowed = new Set(allowedLocationIds.map((id) => String(id)));
  return rows.filter((r) => allowed.has(String(r.location_id)));
}

// Aggregate-context Score tooltip body (ADR 0001 D-5; copy per the feat-234
// design brief §3a, used verbatim). No backend field is needed (no contract
// change), and React does ZERO math.
//
// The copy deliberately never says "percent" and never pins a meaning to the
// number 100: per spec Assumption A3 the backend emits the `%` suffix only when
// the tenant's score_multiplier is 1, so this same component renders a bare
// integer for a multiplier-2 tenant. Every sentence below is true beside `123%`
// AND beside `246` — it asserts only the ratio, the direction, and the cause of
// exceeding 100.
// Exported for testing: react-tooltip only mounts this through its `render` prop
// when the tooltip actually opens, which jsdom can't drive reliably — so the
// feat-234 copy assertions render this component directly.
export const ScoreAggregateTooltipBody: React.FC<{
  multiplier: number;
  anchorDisplay: string;
}> = ({ multiplier, anchorDisplay }) => (
  <div className="rounded-xl overflow-hidden text-left max-w-[300px]">
    <div className="bg-gradient-to-r from-coral to-gold px-4 py-2.5">
      <div className="text-sm font-bold text-white">About this Score</div>
    </div>
    <div className="px-4 py-3 text-ink text-xs space-y-1.5">
      {/* Only THIS sentence differs between branches. At multiplier 1 the score
          IS attempts ÷ visits (the `%` supplies the ×100), so say so precisely.
          At any other multiplier that division is false by two factors, so the
          derivation is dropped and paragraph 3's anchor carries the scale
          instead. */}
      {multiplier === 1 ? (
        <p>
          <span className="font-semibold">Score</span> is every upsell your server attempted
          in this window, divided by the customers they served — the{' '}
          <span className="font-semibold">Visits</span> column.
        </p>
      ) : (
        <p>
          <span className="font-semibold">Score</span> measures every upsell your server
          attempted in this window against the customers they served — the{' '}
          <span className="font-semibold">Visits</span> column — on your restaurant's
          scoring scale.
        </p>
      )}
      <p>
        It is pooled across the whole window, not an average of shift averages, so a busy
        shift counts for more than a quiet one.
      </p>
      {/* The scale anchor, rendered VERBATIM from the backend. This is what makes
          a bare score recoverable: score ÷ anchor = attempts per customer, at any
          multiplier. Never derive it as 100 × multiplier — see the field comment
          in serverScorecardService.ts. */}
      <p>
        <span className="font-semibold">{anchorDisplay}</span> means one upsell attempt for
        every customer served. Many servers go past that by suggesting more than one add-on
        to the same customer — there is no maximum, and higher is better.
      </p>
      <p className="text-inkMuted">
        Open <span className="font-semibold">View Details → Recent Shifts</span> for the
        per-shift breakdown.
      </p>
    </div>
  </div>
);

const ServerScorecard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedTenant } = useTenant();

  // Fake mode is keyed off the ENVIRONMENT (not `config.useDummyData`, which is
  // false in every config here). In fake mode the service returns canned data,
  // so we skip the real location/month SQL probes and the admin tenant gate and
  // render the grid directly (charter: "fake-mode renders both screens").
  const FAKE = config.environment === 'fake';

  // feat-317 (ADR §7.3): one leaderboard grid per effective role. Each group
  // carries what was top-level before: rows, metric_columns, drink_column, and
  // the score multiplier + preformatted scale anchor. Those last two (feat-234)
  // ONLY DRIVE COPY: the multiplier selects which explanatory sentence renders,
  // the anchor is rendered verbatim inside it. Neither is used in any
  // calculation. Defaults match a multiplier-1 tenant so a backend that predates
  // these fields renders today's (correct-at-m=1) wording rather than an empty
  // sentence. Rendered verbatim in response order — never re-sorted or merged.
  const [groups, setGroups] = useState<ServerScorecardGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const { handleError: handleApiError, clearError: clearApiError } = useApiError();

  // Location selection (admin / corporate)
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [availableLocations, setAvailableLocations] = useState<any[]>([]);
  const [isLoadingLocations, setIsLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState<string | null>(null);

  // Month filter ("All" = all-time; specific month windows the Score)
  const [availableMonths, setAvailableMonths] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('All');

  const currentFetchRef = useRef<string | null>(null);
  const [hasRestoredFilters, setHasRestoredFilters] = useState(false);

  const normalizedRole = useMemo(() => {
    const rawRole = user?.managementUser?.role;
    if (!rawRole) return null;
    return rawRole.charAt(0).toUpperCase() + rawRole.slice(1).toLowerCase();
  }, [user?.managementUser?.role]);

  const isAdminOrCorporate = normalizedRole === 'Admin' || normalizedRole === 'Corporate';

  // The location_id we pass to the endpoint. Store managers are pinned
  // server-side, so we only forward the admin/corporate selection.
  const locationFilter = useMemo(() => {
    if (FAKE) return undefined;
    if (isAdminOrCorporate) return selectedLocation || undefined;
    return undefined; // store manager — backend pins to their own location
  }, [FAKE, isAdminOrCorporate, selectedLocation]);

  const tenantFilter = useMemo(() => {
    if (FAKE) {
      // feat-234: fake mode carries TWO tenant shapes, because the tenant's score
      // multiplier decides both the `%` suffix and which explanatory copy branch
      // renders. Default `fake` is the multiplier-1 pilot; `?tenant=fake-m2`
      // selects the multiplier-2 shape that five of the seven live tenants
      // actually run, so a reviewer in `start:fake` can see the majority case
      // instead of only the minority one. FAKE-MODE ONLY — every other
      // environment ignores the query string and resolves the tenant from auth.
      // feat-317: `?tenant=fake-mr` is the two-role tenant (one grid per role).
      const requested = new URLSearchParams(location.search).get('tenant');
      return requested === FAKE_M2_TENANT_ID || requested === FAKE_MR_TENANT_ID ? requested : 'fake';
    }
    return selectedTenant || user?.managementUser?.tenant_id || undefined;
  }, [FAKE, location.search, selectedTenant, user?.managementUser?.tenant_id]);

  // Convert a YYYY-MM month into an inclusive [startDate, endDate] window.
  const getDateRangeForMonth = (month: string): { startDate?: string; endDate?: string } => {
    if (!month || month === 'All') return {};
    const parts = month.split('-');
    if (parts.length !== 2) return {};
    const year = parseInt(parts[0], 10);
    const monthNum = parseInt(parts[1], 10);
    if (isNaN(year) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) return {};
    const startDate = `${parts[0]}-${parts[1]}-01`;
    const endDateObj = new Date(year, monthNum, 0); // last day of month
    const endDate = `${parts[0]}-${parts[1]}-${String(endDateObj.getDate()).padStart(2, '0')}`;
    return { startDate, endDate };
  };

  const convertToDisplayFormat = (monthStr: string): string => {
    if (monthStr === 'All') return 'All';
    const parts = monthStr.split('-');
    if (parts.length !== 2) return 'All';
    const date = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1);
    if (isNaN(date.getTime())) return 'All';
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const convertFromDisplayFormat = (displayStr: string): string => {
    if (displayStr === 'All') return 'All';
    const date = new Date(displayStr);
    if (isNaN(date.getTime())) return 'All';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  // Restore filters when returning from the detail page.
  useEffect(() => {
    const savedFilters = location.state?.filters;
    if (savedFilters && !hasRestoredFilters) {
      if (savedFilters.selectedMonth && savedFilters.selectedMonth !== selectedMonth) {
        setSelectedMonth(savedFilters.selectedMonth);
      }
      if (savedFilters.selectedLocation !== undefined && savedFilters.selectedLocation !== selectedLocation) {
        setSelectedLocation(savedFilters.selectedLocation);
      }
      setHasRestoredFilters(true);
    } else if (!savedFilters && hasRestoredFilters) {
      setHasRestoredFilters(false);
    }
  }, [location.state, hasRestoredFilters, selectedMonth, selectedLocation]);

  // Reset location when tenant changes (admin).
  useEffect(() => {
    if (!FAKE && normalizedRole === 'Admin' && selectedTenant) {
      setSelectedLocation(null);
    }
  }, [FAKE, selectedTenant, normalizedRole]);

  // Fetch available locations (admin / corporate).
  useEffect(() => {
    const fetchLocations = async () => {
      if (FAKE || !isAdminOrCorporate) return;
      const { tenant_id } = user?.managementUser || {};
      if (normalizedRole === 'Admin' && !selectedTenant) return;

      setIsLoadingLocations(true);
      setLocationsError(null);
      try {
        let tenantId = selectedTenant;
        if (normalizedRole === 'Corporate' && !tenantId) tenantId = tenant_id || null;
        const response = await managementService.getLocationsByTenant(tenantId || undefined);
        if (response.success && response.locations) {
          // Only show locations that have gone live (bespoke_go_live_date set),
          // mirroring the Training Progress report's location filter.
          const liveLocations = response.locations.filter(
            (l: any) => l.bespoke_go_live_date != null
          );
          setAvailableLocations(liveLocations);
          if (normalizedRole === 'Corporate' && liveLocations.length === 1) {
            setSelectedLocation(liveLocations[0].id);
          }
        } else if (response.error) {
          setLocationsError(response.error);
          setAvailableLocations([]);
        }
      } catch (err: any) {
        setLocationsError(err.message || 'Failed to load locations');
        setAvailableLocations([]);
      } finally {
        setIsLoadingLocations(false);
      }
    };
    fetchLocations();
  }, [FAKE, user?.managementUser, selectedTenant, isAdminOrCorporate, normalizedRole]);

  // Derive months that have shift data (tenant/location scoped) for the filter.
  useEffect(() => {
    const fetchAvailableMonths = async () => {
      if (FAKE) {
        setAvailableMonths(['2026-06', '2026-05', '2026-04']);
        return;
      }
      if (isAdminOrCorporate && isLoadingLocations) return;
      // feat-133 Batch-1: scoped ref-lookup replaces the execute-select
      // available-months query. Tenant is bound server-side from UserContext
      // (store managers pinned); we forward the admin/corporate location filter.
      // The backend preserves the exact predicate (US/Pacific bucketing,
      // verified-interaction join, HAVING COUNT>0) so the month set is identical.
      if (!tenantFilter) {
        setAvailableMonths([]);
        return;
      }
      try {
        const response = await getTenantAvailableMonths(tenantFilter, {
          locationId: locationFilter,
        });
        if (response && response.length > 0) {
          setAvailableMonths(
            response
              .map((row: any) => `${row.year}-${String(row.month).padStart(2, '0')}`)
              .filter((m: string) => {
                const [y, mo] = m.split('-').map((n) => parseInt(n, 10));
                return !isNaN(y) && !isNaN(mo) && mo >= 1 && mo <= 12;
              }),
          );
        } else {
          setAvailableMonths([]);
        }
      } catch (err) {
        console.error('ServerScorecard: error fetching available months:', err);
        setAvailableMonths([]);
      }
    };
    fetchAvailableMonths();
  }, [FAKE, locationFilter, tenantFilter, isAdminOrCorporate, isLoadingLocations]);

  // Fetch the grid from the aggregate endpoint.
  useEffect(() => {
    const fetchGrid = async () => {
      if (!FAKE) {
        if (isAdminOrCorporate && isLoadingLocations) return;
        if (!tenantFilter) {
          // Admin must pick a tenant first; handled by the gate below.
          setIsLoading(false);
          return;
        }
      }

      const { startDate, endDate } = getDateRangeForMonth(selectedMonth);
      const fetchKey = `${tenantFilter || 'fake'}-${locationFilter || 'all'}-${selectedMonth}`;
      if (currentFetchRef.current === fetchKey) return;
      currentFetchRef.current = fetchKey;

      setIsLoading(true);
      setError(null);
      setForbidden(false);
      clearApiError();
      try {
        const resp = await getServerScorecard(tenantFilter || 'fake', {
          locationId: locationFilter,
          startDate,
          endDate,
        });
        if (currentFetchRef.current === fetchKey) {
          // An old backend (no `groups`) → one legacy group from the top level.
          setGroups(
            groupsOf(resp, (r): ServerScorecardGroup => ({
              role: null,
              display_name: null,
              metric_columns: r.metric_columns,
              drink_column: r.drink_column,
              rows: r.rows,
              score_windowing: r.score_windowing,
              score_multiplier: r.score_multiplier,
              score_scale_anchor_display: r.score_scale_anchor_display,
            })),
          );
        }
      } catch (err) {
        if (currentFetchRef.current !== fetchKey) return;
        if (err instanceof ServerScorecardForbiddenError) {
          setForbidden(true);
          setGroups([]);
        } else {
          const msg = err instanceof Error ? err.message : 'Failed to load server scorecard';
          setError(msg);
          handleApiError(err);
        }
      } finally {
        if (currentFetchRef.current === fetchKey) {
          setIsLoading(false);
          currentFetchRef.current = null;
        }
      }
    };
    fetchGrid();
  }, [FAKE, tenantFilter, locationFilter, selectedMonth, isAdminOrCorporate, isLoadingLocations, clearApiError, handleApiError]);

  // feat-097 Part A — scope the grid to the Location-filter's location set.
  // With "All Locations" selected, the aggregate endpoint returns EVERY server in
  // the tenant, including servers whose home location isn't one the tenant
  // actually offers in the Location dropdown (test / not-yet-live / decommissioned
  // locations) — these clutter the report as all-"—" rows. The dropdown is already
  // gated to live locations (#81); here we constrain the rows to that same set.
  // Client-side only — no contract/auth change. A specific-location selection is
  // already narrowed server-side, and store-manager / fake modes have no dropdown
  // to scope against, so both pass through unchanged.
  //
  // feat-317: the scope is applied PER GROUP, then `visibleGroups` drops a role
  // grid the scope emptied (no empty per-role grid, D6).
  const shownGroups = useMemo(() => {
    const scoping = !(FAKE || !isAdminOrCorporate || selectedLocation) && !isLoadingLocations;
    const allowed = availableLocations.map((l) => l.id);
    return visibleGroups(
      groups,
      (g) => g.rows,
      (g) => (scoping ? { ...g, rows: scopeRowsToLocationSet(g.rows, allowed) } : g),
    );
  }, [FAKE, isAdminOrCorporate, selectedLocation, isLoadingLocations, availableLocations, groups]);
  const hasRows = shownGroups.some((g) => g.rows.length > 0);
  const multiGrid = showRoleHeadings(shownGroups);
  // Copy inputs. Nullish-coalesce, not ||: a multiplier of 0 is nonsense but an
  // empty anchor string would silently blank the sentence, and ?? keeps the
  // fallback to exactly the "field absent" case.
  const scaleOf = (g: ServerScorecardGroup | undefined) => ({
    multiplier: g?.score_multiplier ?? 1,
    anchor: g?.score_scale_anchor_display ?? '100%',
  });
  const firstScale = scaleOf(shownGroups[0]);
  // The legend + tooltip render ONCE (design NOTES §3) unless the visible role
  // grids disagree on scale, in which case each grid carries its own legend.
  const scalesDiffer = shownGroups.some((g) => {
    const sc = scaleOf(g);
    return sc.multiplier !== firstScale.multiplier || sc.anchor !== firstScale.anchor;
  });

  // feat-234 removed the "#"/"%" display-mode toggle (and the shared
  // `useMetricDisplayMode` hook behind it). Every metric column is now COUNTS,
  // always. `metric_percents` / `drink_percent` are still in the response and
  // still computed — deliberately, so percentages are cheap to switch back on for
  // a tenant whose servers work a fixed station — they are simply not rendered.
  // At Ladle & Leaf servers rotate between stations and we do not record which,
  // so a per-metric percentage divides by visits where the metric was
  // structurally impossible and maligns the server.

  const handleViewDetails = (row: ServerScorecardRow) => {
    navigate('/restaurant/server-performance-history', {
      state: {
        serverData: {
          serverName: row.name,
          serverId: row.server_id,
          tenantId: tenantFilter || null,
          overallScore: row.score,
        },
        filters: {
          selectedMonth,
          selectedLocation: locationFilter || null,
          selectedTenant: tenantFilter || null,
        },
      },
    });
  };

  // ─── render gates ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <LoadingSpinner message="Loading server performance data..." />
        </div>
      </div>
    );
  }

  const isAdminWithoutTenant = !FAKE && normalizedRole === 'Admin' && !selectedTenant;
  if (isAdminWithoutTenant) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <div className="max-w-md mx-auto text-center">
            <div className="bg-white rounded-2xl shadow-sm border border-softBorder p-8">
              <h3 className="text-lg font-semibold text-ink mb-2 font-display">Select a Tenant</h3>
              <p className="text-inkMuted mb-4 text-sm">
                Please select a tenant from the dropdown in the navigation bar to view server
                performance data.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !FAKE) {
    return (
      <div className="min-h-screen bg-cream">
        <ScorecardNavigation />
        <div className="p-6">
          <ErrorMessage
            title="Unable to Load Server Scorecard"
            message="We're having trouble loading the server performance data. This could be due to database connectivity issues."
            details={error}
            onRetry={() => {
              setError(null);
              window.location.reload();
            }}
            onGoBack={() => navigate('/restaurant/leaderboard')}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="mb-8 flex flex-wrap justify-between items-start gap-4">
          <div>
            <h1 className="text-4xl font-display font-semibold text-ink mb-1">Server Scorecard</h1>
            <p className="text-sm text-inkMuted">Review performance across your servers</p>
          </div>
          <div className="flex items-center space-x-4">
            {isAdminOrCorporate && !FAKE && (
              <div className="flex flex-col">
                <div className="flex items-center space-x-2">
                  <label htmlFor="location-select" className="text-sm font-semibold text-ink">
                    Location:
                  </label>
                  <select
                    id="location-select"
                    value={selectedLocation || ''}
                    onChange={(e) => setSelectedLocation(e.target.value || null)}
                    className={`px-3 py-2 border rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-coral/40 focus:border-coral ${
                      locationsError ? 'border-red-300' : 'border-softBorder'
                    }`}
                    disabled={isLoadingLocations || !!locationsError}
                  >
                    <option value="">All Locations</option>
                    {availableLocations.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name}
                      </option>
                    ))}
                  </select>
                  {isLoadingLocations && <div className="text-sm text-inkMuted">Loading...</div>}
                </div>
                {locationsError && <div className="text-xs text-coral mt-1">{locationsError}</div>}
              </div>
            )}

            {/* feat-234: the #/% toggle that sat here is deleted. No spacer and no
                justify-* change is needed — the wrapper uses `space-x-4`, which puts
                the margin on each child but the first, so a deleted child takes its
                own spacing with it and the survivors close to the same rhythm.
                The "Period:" label mirrors the "Location:" pattern above: for a store
                manager (the most common view) MonthFilter is now the ONLY control in
                this header, and unlabelled it reads as an ambiguous "All" button. */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-semibold text-ink">Period:</label>
              <MonthFilter
                availableMonths={availableMonths.map(convertToDisplayFormat)}
                selectedMonth={convertToDisplayFormat(selectedMonth)}
                onMonthChange={(displayMonth) => setSelectedMonth(convertFromDisplayFormat(displayMonth))}
              />
            </div>
          </div>
        </div>

        {forbidden ? (
          <div className="rounded-2xl bg-white border border-softBorder p-10 text-center">
            <h3 className="font-display text-lg text-ink mb-1">Server scorecard unavailable</h3>
            <p className="text-inkMuted text-sm">This view isn't available for the selected tenant.</p>
          </div>
        ) : !hasRows ? (
          <div className="text-center py-12">
            <div className="text-ink text-lg font-display">No scorecard data available</div>
            <div className="text-inkMuted text-sm mt-2">
              Check back later for server performance data
            </div>
          </div>
        ) : multiGrid ? (
          // feat-317: one grid per role, headed by its display_name (design NOTES
          // §4.3). Dense vs comfortable is decided PER GRID from that role's own
          // metric_columns. Each grid's pinned header releases at its own card.
          <div className="space-y-6">
            {shownGroups.map((g, i) => (
              <div key={g.role ?? `group-${i}`}>
                <RoleGridHeading as="h2" displayName={g.display_name} role={g.role} />
                <ScorecardGrid group={g} onViewDetails={handleViewDetails} tooltipContent={scalesDiffer ? `agg:${i}` : 'agg'} />
                {scalesDiffer && (
                  <ScoreLegend multiplier={scaleOf(g).multiplier} anchor={scaleOf(g).anchor} />
                )}
              </div>
            ))}
          </div>
        ) : (
          <ScorecardGrid group={shownGroups[0]} onViewDetails={handleViewDetails} />
        )}

        {/* Legend — copy per the feat-234 design brief §3b, used verbatim (see
            ScoreLegend). Renders OUTSIDE the empty/forbidden branches, so it must
            parse standing alone with no table above it. feat-317: once, below the
            last grid, unless role grids disagree on scale (then under each grid). */}
        {!(multiGrid && scalesDiffer) && (
          <ScoreLegend multiplier={firstScale.multiplier} anchor={firstScale.anchor} />
        )}

        {/* feat-091 Slice C — single aggregate Score tooltip instance for the grid. */}
        <Tooltip
          id={SCORE_TOOLTIP_ID}
          place="top"
          opacity={1}
          clickable
          style={{
            padding: 0,
            background: '#ffffff',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            zIndex: 60,
            maxWidth: '320px',
          }}
          render={({ content }) => {
            // feat-317: `agg:<i>` addresses group i when role grids differ in scale.
            const m = /^agg:(\d+)$/.exec(content ?? '');
            const sc = m ? scaleOf(shownGroups[Number(m[1])]) : firstScale;
            return <ScoreAggregateTooltipBody multiplier={sc.multiplier} anchorDisplay={sc.anchor} />;
          }}
        />
      </div>
    </div>
  );
};

// ── feat-317: one role's leaderboard grid. Dense vs comfortable is decided from
//    THIS group's metric_columns (design NOTES §4.3), so one role can be dense and
//    another comfortable on the same page. With one group this is exactly the
//    pre-feat-317 markup. ─────────────────────────────────────────────────────────
const ScorecardGrid: React.FC<{
  group: ServerScorecardGroup;
  onViewDetails: (row: ServerScorecardRow) => void;
  tooltipContent?: string;
}> = ({ group, onViewDetails, tooltipContent = 'agg' }) => {
  const rows = group.rows;
  const metricColumns: MetricColumn[] = group.metric_columns || [];
  const drinkColumn: DrinkColumn | null = group.drink_column ?? null;
  if (metricColumns.length > DENSE_AFTER) {
    // feat-296: 5–7 metric columns → the dense grid (design/NOTES.md). At ≤4
    // the comfortable markup below renders unchanged.
    return (
      <DenseScorecardTable
        rows={rows}
        metricColumns={metricColumns}
        drinkColumn={drinkColumn}
        onViewDetails={onViewDetails}
        tooltipContent={tooltipContent}
      />
    );
  }
  return (
    // feat-297: `overflow-clip`, not `overflow-hidden` — a hidden-overflow
    // card would capture the pinned header's `sticky` (design NOTES §1e).
    <div className="bg-white rounded-[20px] shadow-lg overflow-clip">
      {/* Padding/width tightened per design v2 (ADR 0002): at 9 cols the
          as-shipped px-6/px-5 + w-[15%] overflows at 1024px (measured
          +84px). px-3 on data cols + min-w (no forced %) fits 961/961. */}
      <PinnedHeaderTable
        scrollerClassName="overflow-x-auto"
        tableClassName="min-w-full text-sm"
        stripClassName="rounded-t-[20px] data-[pinned=true]:rounded-t-none"
        thead={
          <thead className="bg-gradient-to-r from-coral via-gold to-coral align-bottom">
            <tr>
              <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                Server
              </th>
              {/* feat-234: Score is right-aligned. It now carries a `%` suffix,
                  and `tabular-nums` cannot align it — neither Fredoka (the
                  font-display face the Score cell uses) nor Nunito ships a
                  `tnum` GSUB feature, so that class has always been inert here,
                  and Fredoka's "1" is 34% narrower than its "2". Left-aligned,
                  the repeated % glyph lands at a different x on every row and
                  reads as broken. */}
              <th className="px-3 py-4 text-right text-xs font-bold text-white uppercase tracking-wider">
                Score
              </th>
              <th className="px-3 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                Visits
              </th>
              {metricColumns.map((col) => (
                <th
                  key={col.key}
                  className="px-3 py-4 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[64px]"
                >
                  {col.label}
                </th>
              ))}
              {/* Optional drink column (ADR 0002 U2) — after metrics, before Details.
                  The normal-case "(drink)" qualifier distinguishes it from a
                  metric that shares the same configured name (e.g. "Dubai Chocolate"). */}
              {drinkColumn?.enabled && (
                <th className="px-3 py-4 text-left text-xs font-bold text-white uppercase tracking-wider min-w-[64px]">
                  {drinkColumn.label}{' '}
                  <span className="normal-case font-normal text-white/80">(drink)</span>
                </th>
              )}
              <th className="px-4 py-4 text-left text-xs font-bold text-white uppercase tracking-wider">
                Details
              </th>
            </tr>
          </thead>
        }
      >
          <tbody className="bg-white divide-y divide-softBorder">
            {rows.map((row) => (
              <tr key={row.server_id} className="hover:bg-cream-dark/40 transition-colors">
                <td
                  className="px-4 py-4 text-sm font-semibold text-ink max-w-[150px] truncate"
                  title={row.name}
                >
                  {row.name}
                </td>
                {/* Score — plain NEUTRAL value (O1); tier/band intentionally not rendered.
                    feat-091 Slice C: the Score cell (and ONLY the Score cell) gains the
                    aggregate-context tooltip explaining how the window figure is formed.
                    feat-234: right-aligned (see the header comment) and the string is the
                    backend's pooled `score_display` verbatim — `%` suffix included; React
                    never concatenates the suffix and never divides. */}
                <td className="px-3 py-4 text-right whitespace-nowrap">
                  <ScoreValue display={row.score_display} tooltipContent={tooltipContent} />
                </td>
                <td className="px-3 py-4 whitespace-nowrap">
                  {/* feat-093 Slice B: Visits sourced from backend
                      `visit_count` (greeting-anchored, feat-092), with the
                      v:2 `greetings` fallback. NULLISH (??) so a real 0
                      shows 0 and a no-in-window-shifts row (null) shows "—".
                      Backend single-sources this — no React math. */}
                  <NumCell value={row.visit_count ?? row.greetings} />
                </td>
                {metricColumns.map((col) => (
                  <td key={col.key} className="px-3 py-4 whitespace-nowrap">
                    <NumCell value={row.metric_totals?.[col.key] ?? null} />
                  </td>
                ))}
                {drinkColumn?.enabled && (
                  <td className="px-3 py-4 whitespace-nowrap">
                    <NumCell value={row.drink_total ?? null} />
                  </td>
                )}
                <td className="px-4 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => onViewDetails(row)}
                    className="text-coral hover:text-coral/80 font-semibold text-sm"
                  >
                    View Details →
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
      </PinnedHeaderTable>
    </div>
  );
};

// ── Score legend — copy per the feat-234 design brief §3b, used verbatim. Visits
//    and the metric columns merge into one clause because they are now the same
//    kind of number; stating them separately was an artefact of the deleted
//    toggle. ───────────────────────────────────────────────────────────────────
const ScoreLegend: React.FC<{ multiplier: number; anchor: string }> = ({ multiplier, anchor }) => (
    <p className="mt-3 px-1 text-[11px] text-inkMuted">
      <span className="font-semibold">Score</span> ={' '}
      {multiplier === 1
        ? 'total upsell attempts ÷ total visits in the window'
        : "total upsell attempts measured against total visits in the window, on your restaurant's scoring scale"}
      , so a busy shift counts for more than a quiet one.{' '}
      <span className="font-semibold">{anchor}</span> = one attempt per customer served;
      there is no maximum and higher is better.{' '}
      <span className="font-semibold">Visits</span> and each metric column ={' '}
      <span className="font-semibold">totals</span> across the window.{' '}
      <span className="font-semibold">—</span> = no shifts in this window; a real{' '}
      <span className="font-semibold">0</span> = had shifts, no hits on that metric.
    </p>
);

// ── Score cell content — the backend's pooled `score_display`, VERBATIM (feat-234),
//    or a muted em-dash when the server had no in-window shifts. Shared by both
//    grid branches so the Score cell cannot drift between them. ─────────────────
const ScoreValue: React.FC<{ display: string | null | undefined; tooltipContent?: string }> = ({
  display,
  tooltipContent = 'agg',
}) =>
  display && display !== '—' ? (
    <span
      data-tooltip-id={SCORE_TOOLTIP_ID}
      data-tooltip-content={tooltipContent}
      className="font-display text-lg font-semibold text-ink tabular-nums cursor-help underline decoration-dotted underline-offset-4"
    >
      {display}
    </span>
  ) : (
    <span className="font-display text-lg text-inkMuted/50" title="No shifts in this window">
      —
    </span>
  );

// ── feat-296 DENSE grid (≥5 metric columns) — design/NOTES.md §3/§4. ─────────────
// Class strings are literal (Tailwind purges interpolated names). Do not
// "simplify" them; each non-obvious class is explained in NOTES §4:
//  - label spans have NO `block` (it would defeat line-clamp's -webkit-box);
//  - `min-w-min` lets a single over-long word widen its own column;
//  - the drink label clamps to 2 lines so "(drink)" can never be cut off;
//  - `from-10%` keeps the gradient flat coral under the frozen Server header;
//  - sticky cells are opaque in both states (`bg-white group-hover:bg-cream`);
//  - `border-separate border-spacing-0` + per-cell border-t, because collapsed
//    borders do not move with sticky cells (feat-241 rule 6);
//  - `relative` on the scroller contains absolute descendants (feat-241 rule 2).
// feat-297: the header IS vertically pinned, beneath ScorecardNavigation. A plain
// `sticky top-*` <thead> would stick to the x-scroller, not the page, so
// <PinnedHeaderTable> draws the thead a second time in a page-sticky strip
// (z-30, inline `top` = the nav's live measured height) above the scroller,
// `scrollLeft`-synced to it, with column widths measured from the body table.
// The frozen SERVER corner cell keeps its classes inside that strip: the strip
// is itself a (non-user-scrollable) scroll container, so `sticky left-0` holds
// there too, and `z-20` resolves inside the strip's own stacking context,
// above the header cells scrolling under it. The card is `overflow-clip` (not
// `-hidden`, which would capture the sticky). The body table's own thead stays
// in the DOM, clipped under the strip, as the single copy exposed to assistive
// tech; the pinned copy is aria-hidden. Header tooltips are native `title` on
// both copies; the Score tooltip stays mounted outside the card.
// See stickyTableHeader/PinnedHeaderTable.tsx.
const DENSE_TH =
  'px-3 pt-4 pb-3 align-bottom text-xs font-bold text-white uppercase tracking-wider whitespace-nowrap';
const DENSE_TH_METRIC =
  'px-2.5 pt-4 pb-3 align-bottom text-left text-xs font-bold text-white uppercase tracking-wider min-w-[96px]';

const DenseScorecardTable: React.FC<{
  rows: ServerScorecardRow[];
  metricColumns: MetricColumn[];
  drinkColumn: DrinkColumn | null | undefined;
  onViewDetails: (row: ServerScorecardRow) => void;
  tooltipContent?: string;
}> = ({ rows, metricColumns, drinkColumn, onViewDetails, tooltipContent = 'agg' }) => (
  <div className="bg-white rounded-[20px] shadow-lg overflow-clip">
    <PinnedHeaderTable
      scrollerClassName="relative overflow-x-auto overscroll-x-contain"
      tableClassName="w-max min-w-full border-separate border-spacing-0 text-sm"
      stripClassName="rounded-t-[20px] data-[pinned=true]:rounded-t-none"
      thead={
        <thead className="bg-gradient-to-r from-coral from-10% via-gold to-coral">
          <tr>
            <th className="sticky left-0 z-20 bg-coral px-4 pt-4 pb-3 align-bottom text-left text-xs font-bold text-white uppercase tracking-wider shadow-[1px_0_0_0_rgba(0,0,0,0.08)]">
              Server
            </th>
            <th className={`${DENSE_TH} text-right`}>Score</th>
            <th className={`${DENSE_TH} text-left`}>Visits</th>
            {metricColumns.map((col) => (
              <th key={col.key} className={DENSE_TH_METRIC} title={col.label}>
                <span className="max-w-[76px] min-w-min line-clamp-3 leading-4">{col.label}</span>
              </th>
            ))}
            {drinkColumn?.enabled && (
              <th className={DENSE_TH_METRIC} title={`${drinkColumn.label} (drink)`}>
                <span className="max-w-[76px] min-w-min line-clamp-2 leading-4">
                  {drinkColumn.label}
                </span>
                <span className="block normal-case font-normal tracking-normal leading-4 text-white/80">
                  (drink)
                </span>
              </th>
            )}
            <th className={`${DENSE_TH} text-left`}>Details</th>
          </tr>
        </thead>
      }
    >
        <tbody className="bg-white [&>tr:not(:first-child)>*]:border-t [&>tr:not(:first-child)>*]:border-softBorder">
          {rows.map((row) => (
            <tr key={row.server_id} className="group hover:bg-cream transition-colors">
              <td className="sticky left-0 z-10 bg-white group-hover:bg-cream transition-colors px-4 py-4 text-sm font-semibold text-ink shadow-[1px_0_0_0_rgba(0,0,0,0.08)]">
                <div className="w-[104px] sm:w-[116px] truncate" title={row.name}>
                  {row.name}
                </div>
              </td>
              <td className="px-3 py-4 text-right whitespace-nowrap">
                <ScoreValue display={row.score_display} tooltipContent={tooltipContent} />
              </td>
              <td className="px-3 py-4 whitespace-nowrap">
                <NumCell value={row.visit_count ?? row.greetings} />
              </td>
              {metricColumns.map((col) => (
                <td key={col.key} className="px-2.5 py-4 whitespace-nowrap">
                  <NumCell value={row.metric_totals?.[col.key] ?? null} />
                </td>
              ))}
              {drinkColumn?.enabled && (
                <td className="px-2.5 py-4 whitespace-nowrap">
                  <NumCell value={row.drink_total ?? null} />
                </td>
              )}
              <td className="px-3 py-4 whitespace-nowrap text-sm">
                <button
                  onClick={() => onViewDetails(row)}
                  className="text-coral hover:text-coral/80 font-semibold text-sm"
                >
                  View Details →
                </button>
              </td>
            </tr>
          ))}
        </tbody>
    </PinnedHeaderTable>
  </div>
);

// ── Numeric grid cell — a real number (incl. 0) in ink, or a muted em-dash when
//    the value is null (server had 0 in-window shifts). The two cases are
//    deliberately distinct (ADR D-7 null semantics): a real 0 means "had shifts,
//    no hits"; "—" means "no shifts in this window". ─────────────────────────────
const NumCell: React.FC<{ value: number | null }> = ({ value }) => {
  if (value === null || value === undefined) {
    return (
      <span className="text-sm text-inkMuted/50" title="No shifts in this window">
        —
      </span>
    );
  }
  return <span className="text-sm text-ink tabular-nums">{value.toLocaleString()}</span>;
};

// feat-234: the #/%-aware `MetricValueCell` is deleted. Every metric and drink
// cell now renders its backend `count` through `NumCell` above — the same
// component the Visits column already used, and byte-identical to what
// MetricValueCell emitted in count mode.

export default ServerScorecard;
